import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { getUnitWiseCost } from "../../redux/adminUnitReportSlice/adminUnitReportSlice";

/* -------------------------------------------------------------------------- */
/*                                   HELPERS                                  */
/* -------------------------------------------------------------------------- */

export const toNumber = (value) => {
    const cleaned = String(value ?? "")
        .replace(/,/g, "")
        .trim();

    const number = parseFloat(cleaned);

    return Number.isFinite(number)
        ? number
        : 0;
};

export const makeProjectKey = (ioNo, revNo) => {
    const io = String(ioNo ?? "").trim();
    const rev = String(revNo ?? "0").trim();

    return `${io}-${rev}`;
};

/* -------------------------------------------------------------------------- */
/*                                    HOOK                                    */
/* -------------------------------------------------------------------------- */

/**
 * Fetches unit-wise-cost data for ONE chart instance, completely independent
 * of any other instance using this same hook. Nothing is written to shared
 * redux state - the thunk result is unwrapped straight into local state, so
 * a bar chart on CRORE and a doughnut chart on PERCENT can never clash.
 *
 * @param {object} params
 * @param {string} params.costLevel        - DIRECT_MATERIAL | CONVERSION_COST | TOTAL_COST
 * @param {string} params.valueMode         - PERCENT | CRORE
 * @param {string} params.selectedFY        - "OVERALL" or a specific FY label (client-side filter only)
 * @param {string[]} params.selectedProjectKeys
 * @param {Map} params.projectKeyToProject  - key -> { IO_NO, REV_NO }
 */
export const useUnitWiseCostData = ({
    costLevel,
    valueMode,
    selectedFY,
    selectedProjectKeys,
    projectKeyToProject,
}) => {

    const dispatch = useDispatch();

    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Guards against a stale response overwriting a newer one if requests
    // resolve out of order (e.g. user flips filters quickly).
    const requestIdRef = useRef(0);
    const projectKeyToProjectRef = useRef(projectKeyToProject);
    projectKeyToProjectRef.current = projectKeyToProject;

    useEffect(() => {

        // const projectsPayload = selectedProjectKeys
        //     .map((key) => projectKeyToProject.get(key))
        const projectsPayload = selectedProjectKeys
            .map((key) => projectKeyToProjectRef.current.get(key))
            .filter(Boolean)
            .map(({ IO_NO, REV_NO }) => ({
                IO_NO,
                REV_NO,
            }));

        const currentRequestId = ++requestIdRef.current;

        setLoading(true);
        setError(null);

        dispatch(
            getUnitWiseCost({
                PROJECT_TYPE: "ALL",
                COST_LEVEL: costLevel,
                VALUE_MODE: valueMode,
                ...(projectsPayload.length > 0 ? { projects: projectsPayload } : {}),
            })
        )
            .unwrap()
            .then((res) => {
                if (currentRequestId !== requestIdRef.current) return; // stale
                setReportData(res?.data ?? res ?? null);
            })
            .catch((err) => {
                if (currentRequestId !== requestIdRef.current) return;
                setError(err);
                setReportData(null);
            })
            .finally(() => {
                if (currentRequestId !== requestIdRef.current) return;
                setLoading(false);
            });

        // Note: selectedFY intentionally excluded - the API returns all FY
        // buckets in one payload, FY selection just picks which bucket to
        // read on the client (see sortedUnitData below).
    }, [
        dispatch,
        costLevel,
        valueMode,
        selectedProjectKeys.join(","),
    ]);

    // const apiUnits = reportData?.units ?? [];
    const DEBUG_DUMMY_UNITS = false; //  flip to false / remove before shipping

    const apiUnits = DEBUG_DUMMY_UNITS
        ? Array.from({ length: 40 }, (_, i) => ({
            MNF_UNIT: `U${i + 1}`,
            UNIT_NAME: `Test Unit ${i + 1} - Manufacturing Plant`,
            UNIT_TYPE: i % 2 === 0 ? "OWN" : "OUTSOURCED",
            total: (Math.random() * 10 + 0.5).toFixed(2),
            financial_years: {},
        }))
        : reportData?.units ?? [];
    const overall = reportData?.overall ?? null;
    const financialYearLabels = reportData?.labels ?? [];

    const MIN_FY_START_YEAR = 2026; // data only exists from FY 2026-27 onward

    const financialYearOptions = useMemo(() => {

        return [
            { label: "Overall", value: "OVERALL" },
            ...financialYearLabels
                .filter((fy) => Number(String(fy).split("-")[0]) >= MIN_FY_START_YEAR)
                .map((fy) => ({
                    label: fy,
                    value: fy,
                })),
        ];

    }, [financialYearLabels]);

    const sortedUnitData = useMemo(() => {

        return [...apiUnits]
            .map((unit) => {

                let value = 0;

                if (!selectedFY || selectedFY === "OVERALL") {
                    value = toNumber(unit.total);
                } else {
                    value = toNumber(unit.financial_years?.[selectedFY]);
                }

                return {
                    unitCode: unit.MNF_UNIT,
                    unitName: unit.UNIT_NAME,
                    unitType: unit.UNIT_TYPE,
                    value,
                };

            })
            .filter((unit) => unit.value > 0)
            .sort((a, b) => b.value - a.value);

    }, [apiUnits, selectedFY]);

    const selectedOverallValue = useMemo(() => {

        if (!overall) return 0;

        if (!selectedFY || selectedFY === "OVERALL") {
            return toNumber(overall.total);
        }

        return toNumber(overall.financial_years?.[selectedFY]);

    }, [overall, selectedFY]);

    return {
        loading,
        error,
        sortedUnitData,
        financialYearOptions,
        selectedOverallValue,
    };
};